/**
 * Pure rules of the chat composer (T-44, PRD §6.3): who will read the message,
 * the per-ticket draft and the send key. Unit-tested in
 * `src/utils/__tests__/composerModel.test.ts`.
 */

export type ComposerAudience =
  | { kind: 'customer'; name: string }
  | { kind: 'discussion' }
  | { kind: 'team' };

/**
 * Who reads what is sent here. Staff writing on a ticket write to the
 * customer by default, or an internal note (`ComposeMode`, D-146);
 * on an organizational discussion, to all its participants; a customer
 * writes to the support team.
 */
export function audienceFor(input: {
  viewerIsStaff: boolean;
  taskType?: string | null;
  customerName?: string | null;
  organizationName?: string | null;
}): ComposerAudience {
  if (!input.viewerIsStaff) return { kind: 'team' };
  if (input.taskType === 'discussion') return { kind: 'discussion' };
  const name = (input.customerName || input.organizationName || '').trim();
  return { kind: 'customer', name };
}

// ── Reply to customer vs internal note (D-146 Q-97, D-144) ──────────────────

/** What one message is: a reply the customer reads, or an internal note only OTE staff read. */
export type ComposeMode = 'reply' | 'internal';

/** The default is always "reply to customer" (D-146). */
export const DEFAULT_COMPOSE_MODE: ComposeMode = 'reply';

/**
 * The two buttons [💬 תשובה ללקוח] / [🔒 הערה פנימית] are offered to OTE
 * staff writing on a customer ticket. A customer never gets them.
 */
export function canWriteInternal(a: ComposerAudience | null | undefined): boolean {
  return a?.kind === 'customer';
}

/** The `is_internal` flag sent with the comment: only an internal note, only where offered. */
export function isInternalSend(mode: ComposeMode, a: ComposerAudience | null | undefined): boolean {
  return mode === 'internal' && canWriteInternal(a);
}

// ── FIX-D251 §1 (D-251): the composer of a CUSTOMER TASK ────────────────────
// A task is never shown in the portal, so "תשובה ללקוח" there reached nobody.
// On a customer task the staff choose: [💬 בצוות] (the default: stays in the
// task, sent exactly as before), [❓ שאלה ללקוח] (also sent to the customer, as
// a portal conversation linked to the task) or [🔒 הערה פנימית] (as before).

export type TaskComposeMode = 'team' | 'question' | 'internal';
export const TASK_COMPOSE_MODES: readonly TaskComposeMode[] = ['team', 'question', 'internal'];
export const DEFAULT_TASK_COMPOSE_MODE: TaskComposeMode = 'team';

/** What a customer-task message sends: only "שאלה ללקוח" carries `to_customer`. */
export function taskSendFlags(mode: TaskComposeMode): { is_internal: boolean; to_customer?: true } {
  if (mode === 'question') return { is_internal: false, to_customer: true };
  return { is_internal: mode === 'internal' };
}

/**
 * FIX-D251b §3 - the "למי זה הולך" line of a question to the customer: the customer's people who
 * will really get it (not the task's own "customer", who may be an OTE employee). `null` while
 * loading; an empty list blocks the send (the server refuses it as well).
 */
export function questionRecipientsLine(names: readonly string[] | null | undefined): { names: string; blocked: boolean } | null {
  if (!names) return null;
  const clean = names.map((n) => n.trim()).filter(Boolean);
  return { names: clean.join(', '), blocked: clean.length === 0 };
}

/** An internal message is never replied to or forwarded (the customer would see the quote). */
export function messageActionsFor(isInternal: boolean): { reply: boolean; forward: boolean } {
  return { reply: !isInternal, forward: !isInternal };
}

/** Customer-facing composers send with Ctrl/⌘+Enter by default, so a stray Enter never reaches a customer. */
export type SendKey = 'enter' | 'mod-enter';
export const SEND_KEY_STORAGE = 'ote_chat_send_key';

export function isCustomerFacing(a: ComposerAudience): boolean {
  return a.kind === 'customer' || a.kind === 'discussion';
}

export function defaultSendKey(a: ComposerAudience): SendKey {
  return isCustomerFacing(a) ? 'mod-enter' : 'enter';
}

/** The remembered choice wins; otherwise the audience's default. */
export function resolveSendKey(stored: string | null | undefined, a: ComposerAudience): SendKey {
  return stored === 'enter' || stored === 'mod-enter' ? stored : defaultSendKey(a);
}

/** Does this keydown send? Shift+Enter is always a new line; Ctrl/⌘+Enter always sends. */
export function isSendKeystroke(
  e: { key: string; shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; isComposing?: boolean },
  pref: SendKey,
): boolean {
  if (e.key !== 'Enter' || e.isComposing || e.shiftKey) return false;
  if (e.ctrlKey || e.metaKey) return true;
  return pref === 'enter';
}

// ── Drafts ───────────────────────────────────────────────────────────────────

export const draftKey = (ticketId: string) => `ote_draft_${ticketId}`;

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Read a draft; storage that throws (private mode) reads as "no draft". */
export function readDraft(store: KeyValueStore | null | undefined, ticketId: string): string {
  try {
    return store?.getItem(draftKey(ticketId)) ?? '';
  } catch {
    return '';
  }
}

/** Save (or clear, when blank) a draft. Returns whether it was stored. */
export function writeDraft(store: KeyValueStore | null | undefined, ticketId: string, text: string): boolean {
  try {
    if (!store) return false;
    if (text.trim()) store.setItem(draftKey(ticketId), text);
    else store.removeItem(draftKey(ticketId));
    return true;
  } catch {
    return false;
  }
}

/** Ctrl / ⌘ as the platform names it. */
export function modKeyLabel(): string {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent) ? '⌘' : 'Ctrl';
}
